/**
 * Customer Portal feature flags surfaced to the SPA.
 *
 * `ENABLE_STOCK_BROWSE` mirrors the API flag of the same name. The public
 * Stock browse UI (list + detail) was removed, so no route reads this flag
 * any more; it is retained because the favourites BFF (`/api/portal/stock/*`)
 * is still gated by `ENABLE_STOCK_BROWSE` on the server.
 *
 * Vite exposes only env vars prefixed with `VITE_` to the browser bundle.
 *
 * Spec:
 *   - PORTAL_REPO_SKELETON_SSOT (no plaintext secrets in the SPA bundle)
 */

/** All feature flags surfaced to the SPA. Names match the API env vars. */
export interface PortalFeatureFlags {
  /** F1 stock browse — Stock Core BFF endpoints + UI surface. */
  ENABLE_STOCK_BROWSE: boolean;
  /** Phase 1 — email + password sign-in. */
  ENABLE_PASSWORD_AUTH: boolean;
  /** Phase 1 — Google OAuth sign-in. */
  ENABLE_OAUTH_GOOGLE: boolean;
  /** Phase 1 — Microsoft OAuth sign-in. */
  ENABLE_OAUTH_MICROSOFT: boolean;
}

/** Read auth-related flags from window or env. */
export interface AuthFeatureFlags {
  passwordAuth: boolean;
  googleOAuth: boolean;
  microsoftOAuth: boolean;
}

function readBoolFromEnv(value: unknown, fallback: boolean): boolean {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  const s = String(value).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
  if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
  return fallback;
}

interface ImportMetaEnvShape {
  VITE_ENABLE_STOCK_BROWSE?: string | boolean;
  VITE_ENABLE_PASSWORD_AUTH?: string | boolean;
  VITE_ENABLE_OAUTH_GOOGLE?: string | boolean;
  VITE_ENABLE_OAUTH_MICROSOFT?: string | boolean;
}

/** Read a Vite env var defensively (Node-only test environments may not have import.meta.env). */
function readEnv(key: string): unknown {
  try {
    const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
    if (env && key in env) return env[key];
  } catch {
    // fall through to the process.env fallback below
  }
  // Vitest (vi.stubEnv) surfaces Vite vars on process.env, not import.meta.env.
  // In the browser bundle `globalThis.process` is undefined, so this is inert.
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  if (proc?.env && key in proc.env) return proc.env[key];
  return undefined;
}

/**
 * Read flags from the build-time Vite env. Tests pass an explicit object
 * to avoid touching `import.meta.env`. Defaults match server-side defaults
 * (every flag opens at `false`).
 */
export function loadFeatureFlags(
  source?: ImportMetaEnvShape | Record<string, string | boolean | undefined>,
): PortalFeatureFlags {
  const env: ImportMetaEnvShape =
    source !== undefined
      ? (source as ImportMetaEnvShape)
      : ((import.meta as unknown as { env: ImportMetaEnvShape }).env ?? {});
  return {
    ENABLE_STOCK_BROWSE: readBoolFromEnv(env.VITE_ENABLE_STOCK_BROWSE, false),
    // Password auth defaults ON — it is the legacy local-account path and
    // we do not want to silently hide it from existing users.
    ENABLE_PASSWORD_AUTH: readBoolFromEnv(env.VITE_ENABLE_PASSWORD_AUTH, true),
    ENABLE_OAUTH_GOOGLE: readBoolFromEnv(env.VITE_ENABLE_OAUTH_GOOGLE, false),
    ENABLE_OAUTH_MICROSOFT: readBoolFromEnv(env.VITE_ENABLE_OAUTH_MICROSOFT, false),
  };
}

/**
 * Module-level singleton — convenient for SPA components that need a quick
 * read. Tests should call `loadFeatureFlags(fixture)` directly to avoid
 * sharing module state.
 */
export const featureFlags: PortalFeatureFlags = loadFeatureFlags();

/** Lightweight helper for components rendering conditional nav links. */
export function isFeatureEnabled<K extends keyof PortalFeatureFlags>(
  flag: K,
  flags: PortalFeatureFlags = featureFlags,
): boolean {
  return flags[flag] === true;
}

/** The production Stock site, used when the deploy does not set VITE_STOCK_FRONTEND_URL. */
export const DEFAULT_STOCK_FRONTEND_URL = 'https://cars-for-sale.providecars.co.jp';

/**
 * Origin of the Stock site this deploy pairs with (no trailing slash):
 * VITE_STOCK_FRONTEND_URL, else the production site. stg sets its own so the
 * header link, the sign-in hand-off and sign-out all stay on stg.
 */
export function getStockFrontendBase(): string {
  const raw = readEnv('VITE_STOCK_FRONTEND_URL');
  const value = typeof raw === 'string' ? raw.trim().replace(/\/+$/, '') : '';
  return value || DEFAULT_STOCK_FRONTEND_URL;
}

/**
 * Absolute URL of the AUCNET Stock registration page, when the deploy sets
 * VITE_STOCK_FRONTEND_URL. Used by the members-only sign-in gate rejection
 * banner to route unregistered users to the sole signup entry (AS /register).
 */
export function getStockRegisterUrl(): string | null {
  const raw = readEnv('VITE_STOCK_FRONTEND_URL');
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  return `${raw.trim().replace(/\/$/, '')}/register`;
}

/**
 * Absolute URL of an AUCNET Stock front vehicle detail page
 * (`/inventory/<vendor vehicle UUID>`), when the deploy sets
 * VITE_STOCK_FRONTEND_URL. Used by the favorites card deep link.
 * Returns null when the base URL is unset (the card renders without a link).
 */
export function getStockVehicleUrl(vendorVehicleId: string): string | null {
  const raw = readEnv('VITE_STOCK_FRONTEND_URL');
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  return `${raw.trim().replace(/\/$/, '')}/inventory/${encodeURIComponent(vendorVehicleId)}`;
}

/**
 * Per-page convenience: which auth methods to surface in the SPA. Tests can
 * override individual flags via `window.__AUTH_FLAGS__` (object).
 */
export function getAuthFeatureFlags(): AuthFeatureFlags {
  let override: Partial<AuthFeatureFlags> | undefined;
  if (typeof window !== 'undefined') {
    override = (window as unknown as { __AUTH_FLAGS__?: Partial<AuthFeatureFlags> }).__AUTH_FLAGS__;
  }
  const f = loadFeatureFlags();
  return {
    passwordAuth: override?.passwordAuth ?? f.ENABLE_PASSWORD_AUTH,
    googleOAuth: override?.googleOAuth ?? f.ENABLE_OAUTH_GOOGLE,
    microsoftOAuth: override?.microsoftOAuth ?? f.ENABLE_OAUTH_MICROSOFT,
  };
}
